import {
  TemplateApprovers,
  TemplateCoreTeamNotificationGroups,
  TemplateNotificationGroups,
  TemplateNotificationRoles,
  TemplateProjectLeaders,
} from "@/containers/ProjectTemplate";
import { IMilestoneTemplate } from "@/interfaces";
import { Modal, Row, Typography } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

interface IProps {
  modalOpen: boolean;
  milestoneTemplate: IMilestoneTemplate;
  onClose(): void;
  saveMilestoneTemplate(milestoneTemplate: IMilestoneTemplate): void;
}

export const MilestoneTemplateModal = ({
  modalOpen,
  milestoneTemplate,
  onClose,
  saveMilestoneTemplate,
}: IProps) => {
  const intl = useIntl();
  const [isValid, setIsValid] = useState({});

  const [milestoneData, setMilestoneData] = useState<IMilestoneTemplate>(
    {} as IMilestoneTemplate
  );

  useEffect(() => {
    setMilestoneData(milestoneTemplate);
  }, [milestoneTemplate]);

  const updateMilestone = (data: Partial<IMilestoneTemplate>): void => {
    const updatedData = { ...milestoneData, ...data };
    setMilestoneData(updatedData as IMilestoneTemplate);
  };

  const onOk = (): void => {
    saveMilestoneTemplate(milestoneData);
    onClose();
  };

  const onCancel = (): void => {
    isValidForm(milestoneTemplate);
    setMilestoneData(milestoneTemplate);
    onClose();
  };

  const isValidForm = (milestoneTemplate: IMilestoneTemplate): void => {
    const isValidRoles = milestoneTemplate.notification?.roles.every(
      (role) => role.roleId && role.region && role.functionalArea
    );

    const isValidApprovers = milestoneTemplate.approvers?.every(
      (approver) =>
        approver.roleId && approver.region && approver.functionalArea
    );

    setIsValid((prev: object) => ({
      ...prev,
      role: isValidRoles,
      approver: isValidApprovers,
    }));
  };

  if (!milestoneTemplate) return null;

  return (
    <Modal
      title={milestoneData.name || milestoneTemplate.name}
      centered
      width="1300px"
      open={modalOpen}
      onCancel={onCancel}
      onOk={onOk}
      okButtonProps={{
        disabled: Object.values(isValid).some((valid) => !valid),
      }}
      destroyOnHidden
    >
      <Row gutter={[24, 24]} className="p-2">
        <>
          <Typography.Title level={5}>
            {intl.formatMessage({
              id: "milestoneTemplates.editModal.approvers",
            })}
          </Typography.Title>
          <TemplateApprovers
            milestoneTemplate={milestoneData}
            updateMilestone={updateMilestone}
            setIsValid={setIsValid}
          />
        </>
        <>
          <Typography.Title level={5}>
            {intl.formatMessage({
              id: "milestoneTemplates.editModal.projectLeaders",
            })}
          </Typography.Title>
          <TemplateProjectLeaders
            milestoneTemplate={milestoneData}
            updateMilestone={updateMilestone}
          />
        </>
        <>
          <Typography.Title level={5}>
            {intl.formatMessage({
              id: "milestoneTemplates.editModal.notificationRoles",
            })}
          </Typography.Title>
          <TemplateNotificationRoles
            milestoneTemplate={milestoneData}
            updateMilestone={updateMilestone}
            setIsValid={setIsValid}
          />
        </>
        <>
          <Typography.Title level={5}>
            {intl.formatMessage({
              id: "milestoneTemplates.editModal.notificationGroups",
            })}
          </Typography.Title>
          <TemplateCoreTeamNotificationGroups
            milestoneTemplate={milestoneData}
            updateMilestone={updateMilestone}
          />
          <TemplateNotificationGroups
            milestoneTemplate={milestoneData}
            updateMilestoneTemplate={updateMilestone}
          />
        </>
      </Row>
    </Modal>
  );
};
